import store from '@src/redux';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { SessionDocument, SessionQuery } from '@src/graphql/generated';
import apolloClient from '@src/graphql/apollo';
import { MEETFRANK_ADMIN_URL } from '@src/constants';
import config from '@src/config/config';
import { captureError } from '@src/util/error';
import { IntercomSettings } from './interfaces/intercomSettings.interface';

const intercomAppId = config.intercomAppId;

export function hideIntercom(): void {
  if (typeof Intercom !== 'undefined') {
    Intercom('hide');

    Intercom('update', {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      hide_default_launcher: true,
    });
  }
}

export function showIntercom(): void {
  if (typeof Intercom !== 'undefined') {
    Intercom('update', {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      hide_default_launcher: false,
    });
  }
}

export const sendNewIntercomMessage = (message: string): void => {
  if (typeof Intercom !== 'undefined') {
    Intercom('showNewMessage', message);
  } else {
    store.dispatch(
      setErrorFlashMessage(
        'Message popup cancelled. Please try disabling any Ad Blockers or contact support@meetfrank.com.',
      ),
    );
  }
};

export const bootIntercom = async (): Promise<void> => {
  if (typeof Intercom === 'undefined') {
    return;
  }

  try {
    if (!intercomAppId) {
      throw new Error('Intercom app id is not set');
    }

    const { data: sessionData } = await apolloClient.query<SessionQuery>({
      query: SessionDocument,
    });

    const recruiter = sessionData.currentRecruiter;
    const company = sessionData.company;
    const companyProfile = company.profile;

    if (recruiter.isDraft || recruiter.isIncomplete) {
      return;
    }

    if (!recruiter.fullName) {
      throw new Error('Recruiter full name missing');
    }

    const intercomSettings: IntercomSettings = {
      'account-company-name': companyProfile.name || undefined,
      'account-country': recruiter.country?.longName || undefined,
      'account-deleted': false,
      'account-last-seen': recruiter.lastSeen || undefined,
      'admin-company-url': `${MEETFRANK_ADMIN_URL}/companies/${company.id}`,
      'admin-recruiter-url': `${MEETFRANK_ADMIN_URL}/recruiters/${recruiter.id}`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'app_id': intercomAppId,
      'company': {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        company_id: company.id,
        name: companyProfile.name || undefined,
      },
      'country': recruiter.country?.longName || undefined,
      'email': recruiter.email || undefined,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'job_title': recruiter.title || undefined,

      'name': recruiter.fullName,
      'phone': recruiter.phone || undefined,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'user_hash': recruiter.intercomHash,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'user_id': recruiter.id,
    };

    Intercom('boot', intercomSettings);
  } catch (error) {
    captureError(new Error('Failed to boot Intercom' + error));
  }
};
